/**
 * Opções dos filtros Empresa e Usuário da aba Meus Estudos (Flux Personalizado, #2000). Regras
 * puras, sem React nem RTK Query: quem chama passa as respostas de `/estudos/empresas/` e
 * `/estudos/criadores/` (#1993) já no formato de domínio.
 *
 * - Empresa (D15): "Todas as empresas" e "Casos Ampere" fixos no topo, depois as demais sem
 *   repetir o id 1, com a contagem histórica em pt-BR (.dc.html:664).
 * - Usuário (D16): quem criou estudos no escopo, sem o próprio usuário (.dc.html:661).
 * - Busca: a do protótipo (.dc.html:661, 665), sem diferenciar maiúsculas, acentos nem espaços
 *   (nas pontas ou repetidos no meio).
 * - Ids repetidos na resposta: fica só o primeiro, porque o teclado e os ids do DOM usam o id como
 *   chave.
 */

import { MyStudyCreatorsQuery } from 'helper/flux/myStudiesAdapter';
import { AMPERE_COMPANY_ID, AMPERE_COMPANY_LABEL } from 'helper/flux/myStudiesFilters';
import removeAccents from 'helper/removeAccents';
import { StudyCompaniesSummary, StudyCreatorOption } from 'types/Flux/Personalized/myStudies';
import {
  MyStudiesCompanyFilter,
  MyStudiesCreatorFilter,
  MyStudiesCreatorUser,
} from 'types/Flux/Personalized/myStudiesFilters';

/** Primeiro item fixo da lista e texto do botão sem empresa escolhida (.dc.html:664, 715). */
export const ALL_COMPANIES_LABEL = 'Todas as empresas';

/** Subtítulo fixo de "Casos Ampere": de onde ele veio (nota 3, .dc.html:46 e 664). */
export const AMPERE_COMPANY_DETAIL = 'Ampere Consultoria · antiga caixa';

/** Item de uma lista de opções da barra (o que o `OptionsPopover` mostra). */
export interface MyStudiesFilterOption {
  /** Chave estável: 'todas' ou o id da empresa ou do usuário. */
  key: string;
  /** Linha principal. */
  label: string;
  /** Linha de baixo (subtítulo); vazio = sem linha. */
  detail: string;
  /** Contagem à direita, já em pt-BR ('2.430'); vazio = sem contagem. */
  count: string;
  /** Item fixo em negrito: Todas as empresas e Casos Ampere (.dc.html:666). */
  emphasized: boolean;
  /** Textos em que a busca procura (ver `filterOptions`). */
  searchText: string[];
}

/** Opção do filtro Empresa, com o valor que vai para `filters.empresa`. */
export interface CompanyFilterOption extends MyStudiesFilterOption {
  value: MyStudiesCompanyFilter;
}

/** Opção do filtro Usuário, com o valor que vai para `filters.criadoPor` (id e nome). */
export interface CreatorFilterOption extends MyStudiesFilterOption {
  value: Required<MyStudiesCreatorUser>;
}

const formatCount = (count: number): string => count.toLocaleString('pt-BR');

const pluralCompanies = (count: number): string => (
  `${formatCount(count)} ${count === 1 ? 'empresa' : 'empresas'}`
);

/**
 * Forma de comparação da busca: minúsculas, sem acentos e com os espaços normalizados (um só no
 * meio, nenhum nas pontas). O `removeAccents` também tira o que não é ASCII (o "·" do subtítulo
 * de "Casos Ampere"), dos dois lados da comparação.
 */
function normalizeSearch(text: string): string {
  return removeAccents(text).toLowerCase().replace(/\s+/g, ' ').trim();
}

/** Primeira ocorrência de cada id, na ordem da resposta. */
function uniqueById<T extends { id: number }>(items: readonly T[]): T[] {
  const seen = new Set<number>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

/**
 * Busca das listas, a do protótipo (.dc.html:661, 665): o texto aparece em algum dos
 * `searchText` da opção. Vai um pouco além dele: ignora acentos ("geracao" acha "GERAÇÃO"), os
 * espaços nas pontas e os repetidos no meio ("log  energia" acha "LOG ENERGIA").
 *
 * @param options - Opções na ordem da lista.
 * @param query - Texto digitado.
 * @returns As opções que casam, na mesma ordem; todas, se o texto for vazio ou só espaços.
 */
export function filterOptions<T extends MyStudiesFilterOption>(
  options: readonly T[],
  query: string,
): T[] {
  const wanted = normalizeSearch(query);
  if (wanted === '') return [...options];
  return options.filter((option) => (
    option.searchText.some((text) => normalizeSearch(text).includes(wanted))
  ));
}

/**
 * Lista do filtro Empresa (.dc.html:664), na ordem fixa da nota 3:
 * 1. "Todas as empresas", com "N empresas" e o total de estudos;
 * 2. "Casos Ampere" (id 1), com "Ampere Consultoria · antiga caixa" e a contagem do id 1 (0 se a
 *    resposta não o trouxer: o item é fixo);
 * 3. as demais, na ordem da resposta (o backend ordena por nome), sem repetir o id 1 nem um id
 *    que já apareceu.
 *
 * Sem a resposta (carregando ou com erro), os dois fixos continuam escolhíveis, sem contagem e
 * sem o "N empresas": o admin não perde o filtro "Casos Ampere" porque a lista falhou.
 *
 * A busca olha o nome e o subtítulo, como o protótipo: "consultoria" acha "Casos Ampere".
 *
 * @param summary - Resposta de `/estudos/empresas/` (contagens históricas, D15), ou `undefined`
 *   enquanto carrega ou com erro.
 * @returns As opções na ordem da lista: os dois fixos e, com a resposta, as demais empresas.
 */
export function buildCompanyOptions(
  summary: StudyCompaniesSummary | undefined,
): CompanyFilterOption[] {
  const ampere = summary?.options.find((option) => option.id === AMPERE_COMPANY_ID);
  const allDetail = summary ? pluralCompanies(summary.totalCompanies) : '';
  const others = uniqueById(summary?.options ?? [])
    .filter((option) => option.id !== AMPERE_COMPANY_ID)
    .map((option): CompanyFilterOption => ({
      key: String(option.id),
      value: { companyId: option.id },
      label: option.name,
      detail: '',
      count: formatCount(option.studyCount),
      emphasized: false,
      searchText: [option.name],
    }));

  return [
    {
      key: 'todas',
      value: 'todas',
      label: ALL_COMPANIES_LABEL,
      detail: allDetail,
      count: summary ? formatCount(summary.totalStudies) : '',
      emphasized: true,
      searchText: [ALL_COMPANIES_LABEL, allDetail],
    },
    {
      key: String(AMPERE_COMPANY_ID),
      value: { companyId: AMPERE_COMPANY_ID },
      label: AMPERE_COMPANY_LABEL,
      detail: AMPERE_COMPANY_DETAIL,
      count: summary ? formatCount(ampere?.studyCount ?? 0) : '',
      emphasized: true,
      searchText: [AMPERE_COMPANY_LABEL, AMPERE_COMPANY_DETAIL],
    },
    ...others,
  ];
}

/**
 * Nome de uma empresa na resposta de `/estudos/empresas/`.
 *
 * @param summary - Resposta (ou `undefined` enquanto carrega ou com erro).
 * @param companyId - Id procurado.
 * @returns O nome, ou `undefined` sem resposta ou sem a empresa nela.
 */
export function companyName(
  summary: StudyCompaniesSummary | undefined,
  companyId: number,
): string | undefined {
  return summary?.options.find((option) => option.id === companyId)?.name || undefined;
}

/**
 * Texto do botão Empresa (.dc.html:715).
 *
 * @param empresa - `filters.empresa`.
 * @param summary - Resposta de `/estudos/empresas/`, se já chegou.
 * @returns "Todas as empresas", "Casos Ampere" (id 1, sem esperar a resposta), o nome da
 *   empresa ou, sem ele, "#<id>" (o mesmo fallback do chip, #1997).
 */
export function companyFilterLabel(
  empresa: MyStudiesCompanyFilter,
  summary: StudyCompaniesSummary | undefined,
): string {
  if (empresa === 'todas') return ALL_COMPANIES_LABEL;
  if (empresa.companyId === AMPERE_COMPANY_ID) return AMPERE_COMPANY_LABEL;
  return companyName(summary, empresa.companyId) ?? `#${empresa.companyId}`;
}

/**
 * Placeholder da busca de empresas (D15, .dc.html:160).
 *
 * @param summary - Resposta de `/estudos/empresas/`, se já chegou.
 * @returns "Buscar entre 59 empresas"; "Buscar empresa" enquanto não há total.
 */
export function companySearchPlaceholder(summary: StudyCompaniesSummary | undefined): string {
  return summary ? `Buscar entre ${pluralCompanies(summary.totalCompanies)}` : 'Buscar empresa';
}

/**
 * Escopo da lista de criadores (D16, .dc.html:657).
 *
 * @param empresa - `filters.empresa`.
 * @param isAdmin - Papel de quem vê a tela.
 * @returns Admin com uma empresa escolhida: `{ companyId }`. Admin em Todas: `{}` (todas do
 *   escopo). Cliente: sempre `{}`; o backend usa a empresa do JWT e ignoraria o parâmetro.
 */
export function creatorsQueryArgs(
  empresa: MyStudiesCompanyFilter,
  isAdmin: boolean,
): MyStudyCreatorsQuery {
  return isAdmin && empresa !== 'todas' ? { companyId: empresa.companyId } : {};
}

/**
 * Lista do filtro Usuário (.dc.html:661): o nome e, embaixo, a empresa. A busca olha só o nome
 * ("Buscar usuário pelo nome"). Um id repetido na resposta aparece uma vez só (o primeiro).
 *
 * @param creators - Resposta de `/estudos/criadores/`, já no escopo da D16.
 * @param viewerUserId - Usuário logado (`selectUserId`), que sai da lista: ele é "Meus estudos".
 * @returns As opções na ordem da resposta, sem o usuário logado.
 */
export function buildCreatorOptions(
  creators: readonly StudyCreatorOption[],
  viewerUserId: number,
): CreatorFilterOption[] {
  return uniqueById(creators)
    .filter((creator) => creator.id !== viewerUserId)
    .map((creator) => ({
      key: String(creator.id),
      value: { userId: creator.id, nome: creator.name },
      label: creator.name,
      detail: creator.company.name,
      count: '',
      emphasized: false,
      searchText: [creator.name],
    }));
}

/**
 * Nome do usuário escolhido em Criado por, para o botão "Usuário: <nome>" e o chip (#1997).
 *
 * Quem vence quando os dois existem: o nome da lista do servidor, que é o atual (um usuário
 * renomeado aparece com o nome novo). O `nome` guardado no filtro (o da lista no momento da
 * escolha, ou o da sessão) só vale enquanto a lista não chega, se ela falhar ou se não trouxer o
 * usuário. Assim o botão e o chip não piscam o id depois de uma escolha.
 *
 * @param criadoPor - `filters.criadoPor`.
 * @param creators - Resposta de `/estudos/criadores/` no escopo atual, se já chegou.
 * @returns O nome, ou `undefined` nos segmentos fixos e sem nenhum nome conhecido.
 */
export function creatorFilterName(
  criadoPor: MyStudiesCreatorFilter,
  creators: readonly StudyCreatorOption[] | undefined,
): string | undefined {
  if (typeof criadoPor !== 'object') return undefined;
  const listed = creators?.find((creator) => creator.id === criadoPor.userId)?.name.trim();
  return listed || criadoPor.nome?.trim() || undefined;
}
